import { AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';
import {
  ContractDocument,
  DispositionDocument,
  DocumentType,
  ExpiredDocument,
  Practice,
} from '../../../../model';
import documentsRepository from '../../../../repositories/documents/documents-repository';
import { AsyncThunkConfig } from '../../../async-thunk-config';
import {
  getDocumentsListState,
  getRequestStateForDocumentType,
} from './documents-list-selectors';
import {
  DocumentsFilterMap,
  DownloadDocumentsError,
} from './documents-list-types';

export const downloadDocuments = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig<DownloadDocumentsError>
>('download-documents', async (_, thunkApi) => {
  const documentsListState = getDocumentsListState(thunkApi.getState());
  const { documentsFilters } = documentsListState.filter;
  if (documentsFilters.Contract.show) {
    thunkApi.dispatch(downloadContracts());
  }
  if (documentsFilters.Disposition.show) {
    thunkApi.dispatch(downloadDispositions());
  }
  if (documentsFilters.ExpiredDocuments.show) {
    thunkApi.dispatch(downloadExpiredDocuments());
  }
}, {
  condition: (_, { getState }) => {
    const state: any = getState();
    const status = state.features.documentsListStore?.contractsRequestStatus;
    if (status === 'loading') {
      return false;
    }
    return true;
  }
});

export const downloadContracts = createAsyncThunk<
  ContractDocument[],
  void,
  AsyncThunkConfig<DownloadDocumentsError>
>('download-contracts', async (_, thunkApi) => {
  try {
    return await documentsRepository.getContractsList(thunkApi);
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const downloadDispositions = createAsyncThunk<
  Practice<DispositionDocument>[],
  void,
  AsyncThunkConfig<DownloadDocumentsError>
>('download-dispositions', async (_, thunkApi) => {
  try {
    return await documentsRepository.getDispositionsList(thunkApi);
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const downloadExpiredDocuments = createAsyncThunk<
  Practice<ExpiredDocument>[],
  void,
  AsyncThunkConfig<DownloadDocumentsError>
>('download-expired-documents', async (_, thunkApi) => {
  try {
    return await documentsRepository.getExpiredDocumentsList(thunkApi);
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const toggleShowDocumentsType = createAsyncThunk<
  DocumentsFilterMap,
  DocumentType,
  AsyncThunkConfig<void>
>('set-show-documents-type', async (type, thunkApi) => {
  const documentsListState = getDocumentsListState(thunkApi.getState());
  const { documentsFilters } = documentsListState.filter;

  // RequestStatus is used to avoid sending the same request twice while is still running
  const requestStatus = getRequestStateForDocumentType(
    thunkApi.getState(),
    type
  );

  const show = !documentsFilters[type].show;
  if (show && requestStatus !== 'loading') {
    const downloadThunk = downloadThunkForType(type);
    thunkApi.dispatch(downloadThunk());
  }
  return {
    ...documentsFilters,
    [type]: {
      show,
      unreadCount: documentsFilters[type].unreadCount,
    },
  };
});

const downloadThunkForType = (
  documentType: DocumentType
): AsyncThunk<any, void, any> => {
  switch (documentType) {
    case 'Disposition':
      return downloadDispositions;
    case 'ExpiredDocuments':
      return downloadExpiredDocuments;
    case 'Contract':
    default:
      return downloadContracts;
  }
};
